import React, { useEffect, useCallback, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState, AppDispatch } from '../../store';
import {
  initializeChatBot,
  createChatSession,
  setUserContext,
  clearError,
  setConnectionStatus
} from '../../store/chatBotSlice';
import { ChatBotErrorBoundary } from './ErrorBoundary';
import { ChatTrigger } from './ChatTrigger';
import { ChatWindow } from './ChatWindow';
import { chatBotAPI } from '../../services/chatBotAPI';
import './ChatBot.css';

// Default position for draggable trigger (distance from right and bottom)
const DEFAULT_POSITION = { x: 24, y: 24 };

interface ChatBotWidgetProps {
  userId?: string;
  userName?: string;
  userRole?: string;
  environment?: 'sandbox' | 'production';
  onError?: (error: any) => void;
  onSessionEnd?: () => void;
  className?: string;
}

export const ChatBotWidget: React.FC<ChatBotWidgetProps> = ({
  userId,
  userName,
  userRole,
  environment = 'production',
  onError,
  onSessionEnd,
  className = ''
}) => {
  const dispatch = useDispatch<AppDispatch>();

  const {
    isOpen,
    isInitialized,
    sessionId,
    isConnected,
    connectionStatus,
    error,
    unreadCount
  } = useSelector((state: RootState) => state.chatBot);

  // Track if session creation is in progress to prevent duplicates
  const sessionCreationInProgress = React.useRef(false);

  // Draggable state
  const [dragPosition, setDragPosition] = useState(DEFAULT_POSITION);
  const [isDragging, setIsDragging] = useState(false);
  const [wasDragged, setWasDragged] = useState(false); // Track if actual drag movement occurred
  const dragRef = useRef<HTMLDivElement>(null);
  const dragStartRef = useRef<{ x: number; y: number; startX: number; startY: number } | null>(null);

  // Handle drag start
  const handleDragStart = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    // Prevent drag if chat is open
    if (isOpen) return;

    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;

    dragStartRef.current = {
      x: dragPosition.x,
      y: dragPosition.y,
      startX: clientX,
      startY: clientY
    };
    setIsDragging(true);
    setWasDragged(false); // Reset drag flag
  }, [dragPosition, isOpen]);

  // Handle drag move
  useEffect(() => {
    if (!isDragging) return;

    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const handleMove = (e: MouseEvent | TouchEvent) => {
      if (!dragStartRef.current) return;

      const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
      const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;

      const deltaX = dragStartRef.current.startX - clientX;
      const deltaY = dragStartRef.current.startY - clientY;

      // Only consider it a drag if moved more than 5 pixels
      const totalMovement = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
      if (totalMovement > 5) {
        setWasDragged(true);
      }

      // Calculate new position (from right and bottom)
      const newX = Math.max(10, Math.min(window.innerWidth - 80, dragStartRef.current.x + deltaX));
      const newY = Math.max(10, Math.min(window.innerHeight - 80, dragStartRef.current.y + deltaY));

      setDragPosition({ x: newX, y: newY });
    };

    const handleEnd = () => {
      setIsDragging(false);
      dragStartRef.current = null;
      // Keep wasDragged true for a short time to prevent click
      timeoutId = setTimeout(() => setWasDragged(false), 100);
    };

    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleEnd);
    document.addEventListener('touchmove', handleMove);
    document.addEventListener('touchend', handleEnd);

    return () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleEnd);
      document.removeEventListener('touchmove', handleMove);
      document.removeEventListener('touchend', handleEnd);
      // Clear timeout on cleanup to prevent state update after unmount
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isDragging]);

  // Reset position when chat opens (so it animates correctly to default position)
  useEffect(() => {
    if (isOpen) {
      setDragPosition(DEFAULT_POSITION);
    }
  }, [isOpen]);

  // Initialize chat bot on mount
  useEffect(() => {
    if (!isInitialized) {
      // Use props passed from parent (main.tsx already fetches from sessionStorage)
      // No need to duplicate the sessionStorage logic here
      const userContext = {
        userId: userId || '',
        userName: userName || '',
        userRole: userRole || '',
        environment: environment
      };

      dispatch(setUserContext(userContext));
      dispatch(initializeChatBot(userContext));
    }
  }, [dispatch, isInitialized, userId, userName, userRole, environment]);

  // Create session immediately after initialization (not waiting for chat to open)
  // This improves UX by having the session ready when user clicks the trigger
  useEffect(() => {
    if (isInitialized && !sessionId && !sessionCreationInProgress.current) {
      sessionCreationInProgress.current = true;

      // Check health first, then create session
      chatBotAPI.checkServiceHealth().then(health => {
        if (health.status === 'healthy') {
          dispatch(setConnectionStatus('connected'));
          // Create session in background
          dispatch(createChatSession()).finally(() => {
            sessionCreationInProgress.current = false;
          });
        } else {
          dispatch(setConnectionStatus('error'));
          sessionCreationInProgress.current = false;
        }
      }).catch(() => {
        dispatch(setConnectionStatus('error'));
        sessionCreationInProgress.current = false;
      });
    }
    // No longer dependent on isOpen - creates session immediately after initialization
  }, [dispatch, isInitialized, sessionId]);

  // Retry session creation if user opens chat when in error state
  useEffect(() => {
    if (isOpen && !sessionId && (connectionStatus === 'error' || connectionStatus === 'disconnected') && !sessionCreationInProgress.current) {
      sessionCreationInProgress.current = true;

      chatBotAPI.checkServiceHealth().then(health => {
        if (health.status === 'healthy') {
          dispatch(setConnectionStatus('connected'));
          dispatch(createChatSession()).finally(() => {
            sessionCreationInProgress.current = false;
          });
        } else {
          dispatch(setConnectionStatus('error'));
          sessionCreationInProgress.current = false;
        }
      }).catch(() => {
        dispatch(setConnectionStatus('error'));
        sessionCreationInProgress.current = false;
      });
    }
  }, [dispatch, isOpen, sessionId, connectionStatus]);

  // Health check and connection monitoring - Only when chat is open
  useEffect(() => {
    if (!isInitialized || !isOpen) return;

    // Do initial health check when opening
    const doHealthCheck = async () => {
      try {
        const health = await chatBotAPI.checkServiceHealth();

        if (health.status === 'healthy' && connectionStatus !== 'connected') {
          dispatch(setConnectionStatus('connected'));
        } else if (health.status === 'down' && connectionStatus !== 'error') {
          dispatch(setConnectionStatus('error'));
        }
      } catch (error) {
        if (connectionStatus !== 'error') {
          dispatch(setConnectionStatus('error'));
        }
      }
    };

    // Run immediately when opening
    doHealthCheck();

    // Then check every 60 seconds (reduced frequency)
    const healthCheckInterval = setInterval(doHealthCheck, 60000);

    return () => clearInterval(healthCheckInterval);
  }, [dispatch, isInitialized, isOpen, connectionStatus]);

  // Auto-reconnect logic - Only validates existing sessions, doesn't create new ones
  useEffect(() => {
    if ((connectionStatus === 'error' || connectionStatus === 'disconnected') && sessionId) {
      const reconnectTimeout = setTimeout(() => {
        // Only try to validate if we have a session
        chatBotAPI.validateSession(sessionId).then(isValid => {
          if (isValid) {
            dispatch(setConnectionStatus('connected'));
          }
        });
      }, 5000); // Retry after 5 seconds

      return () => clearTimeout(reconnectTimeout);
    }
  }, [dispatch, connectionStatus, sessionId]);

  // Error handling
  const handleError = useCallback((error: Error, errorInfo?: any) => {
    if (onError) {
      onError({ error, errorInfo });
    }

    // Clear errors after some time to allow recovery
    setTimeout(() => {
      dispatch(clearError());
    }, 10000);
  }, [dispatch, onError]);

  // Session cleanup on unmount
  useEffect(() => {
    return () => {
      if (sessionId && onSessionEnd) {
        onSessionEnd();
      }
    };
  }, [sessionId, onSessionEnd]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Toggle chat with Ctrl+Shift+C
      if (event.ctrlKey && event.shiftKey && event.key === 'C') {
        event.preventDefault();
        // dispatch(toggleChat()); // Will implement this action
      }

      // Close chat with Escape key when open
      if (event.key === 'Escape' && isOpen) {
        event.preventDefault();
        // dispatch(closeChat()); // Will implement this action
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  // Don't render if there's a critical error during initialization
  if (error && error.code === 'INIT_FAILED' && !error.recoverable) {
    return null;
  }


  return (
    <ChatBotErrorBoundary onError={handleError}>
      {/* Chat Trigger - Draggable when closed */}
      <div
        ref={dragRef}
        className={`chat-bot-widget ${className}`}
        role="region"
        aria-label="Support Chat Bot"
        aria-live="polite"
        onMouseDown={!isOpen ? handleDragStart : undefined}
        onTouchStart={!isOpen ? handleDragStart : undefined}
        style={{
          position: 'fixed',
          bottom: isOpen ? '16px' : `${dragPosition.y}px`,
          right: isOpen ? '428px' : `${dragPosition.x}px`,
          zIndex: 10000,
          transition: isDragging ? 'none' : 'right 0.5s cubic-bezier(0.4, 0, 0.2, 1), bottom 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
          cursor: isOpen ? 'default' : (isDragging ? 'grabbing' : 'grab'),
          userSelect: 'none'
        }}
      >
        <ChatTrigger
          isOpen={isOpen}
          unreadCount={unreadCount}
          isConnected={isConnected}
          connectionStatus={connectionStatus}
          isDragging={wasDragged}
        />
      </div>

      {/* Chat Window - Shown when open */}
      {isOpen && (
        <ChatWindow
          sessionId={sessionId}
          isConnected={isConnected}
          connectionStatus={connectionStatus}
        />
      )}
    </ChatBotErrorBoundary>
  );
};

// Default export with error boundary
export default ChatBotWidget;